import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Mail, Copy, RefreshCcw, Check, Loader2, Info, ExternalLink,
  Sparkles, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Receipts Forwarding Card
 * ------------------------
 * Shows the workspace's unique `receipts.{slug}@inbox.countbucks.com`
 * address, provides copy-to-clipboard, a rotate button, and the recent
 * import log so admins can verify auto-imports.
 *
 * Backed by:
 *   GET  /api/workspaces/{ws}/receipts-forwarding
 *   POST /api/workspaces/{ws}/receipts-forwarding/rotate
 *   POST /api/workspaces/{ws}/receipts-forwarding/simulate  (test button)
 */
export default function ReceiptsForwardingCard({ workspaceId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [simulating, setSimulating] = useState(false);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!workspaceId) return;
    let alive = true;
    setLoading(true);
    apiFetch(`${API_URL}/api/workspaces/${workspaceId}/receipts-forwarding`)
      .then(async (r) => {
        if (!r.ok) throw new Error(await r.text().catch(() => 'Failed to load'));
        return r.json();
      })
      .then((d) => { if (alive) { setData(d); setLoading(false); setError(null); } })
      .catch((e) => { if (alive) { setError(e.message); setLoading(false); } });
    return () => { alive = false; };
  }, [workspaceId, nonce]);

  const copyAddress = async () => {
    if (!data?.address) return;
    try {
      await navigator.clipboard.writeText(data.address);
      setCopied(true);
      toast.success('Forwarding address copied');
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Copy failed — long-press the address to select it');
    }
  };

  const rotate = async () => {
    if (!window.confirm(
      'Rotate the forwarding address? Any auto-forwarding rules pointing '
      + 'at the current address will need to be updated. Existing receipts '
      + 'already imported are unaffected.',
    )) return;
    setRotating(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${workspaceId}/receipts-forwarding/rotate`,
        { method: 'POST' },
      );
      if (!r.ok) throw new Error(await r.text().catch(() => 'Rotate failed'));
      toast.success('New forwarding address issued');
      setNonce((n) => n + 1);
    } catch (e) {
      toast.error(e.message || 'Rotate failed');
    } finally {
      setRotating(false);
    }
  };

  const simulate = async () => {
    setSimulating(true);
    try {
      const r = await apiFetch(
        `${API_URL}/api/workspaces/${workspaceId}/receipts-forwarding/simulate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            from_email: 'noreply@stripe.com',
            subject: 'Your receipt from Stripe (test) — $6.42',
            body_html: '<h2>Blue Bottle Coffee — $6.42</h2><p>Test receipt injected from CountBucks Settings.</p>',
          }),
        },
      );
      if (!r.ok) throw new Error(await r.text().catch(() => 'Simulate failed'));
      const result = await r.json();
      toast.success(`Test receipt imported (${result.count} doc${result.count === 1 ? '' : 's'})`);
      setNonce((n) => n + 1);
    } catch (e) {
      toast.error(e.message || 'Simulate failed');
    } finally {
      setSimulating(false);
    }
  };

  return (
    <Card data-testid="receipts-forwarding-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Mail className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <CardTitle>Receipt auto-import</CardTitle>
            <CardDescription>
              Forward receipts to this address and CountBucks will auto-import them
              into your Document Inbox. AI extracts vendor, amount, and date.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}
        {error && !loading && (
          <div className="text-sm text-red-600 flex items-start gap-2" data-testid="receipts-forwarding-error">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <>
            {/* The address itself */}
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">
                Your workspace forwarding address
              </div>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={data.address}
                  className="font-mono text-sm bg-muted/40"
                  data-testid="receipts-forwarding-address"
                  onFocus={(e) => e.target.select()}
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copyAddress}
                  aria-label="Copy address"
                  data-testid="receipts-forwarding-copy"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={rotate}
                  disabled={rotating}
                  aria-label="Rotate address"
                  data-testid="receipts-forwarding-rotate"
                  title="Rotate — issue a new address and invalidate the old one"
                >
                  {rotating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCcw className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* How-to */}
            <div className="rounded-lg border border-border/60 bg-muted/30 p-4 text-sm space-y-3">
              <div className="flex items-center gap-2 text-foreground font-medium">
                <Info className="w-4 h-4 text-blue-500" /> How to use it
              </div>
              <ol className="list-decimal pl-5 space-y-1.5 text-muted-foreground">
                <li>
                  <strong className="text-foreground">Forward manually:</strong> hit &ldquo;Forward&rdquo; in any receipt
                  email → paste this address in the &ldquo;To&rdquo; field → send.
                </li>
                <li>
                  <strong className="text-foreground">Auto-forward from Gmail:</strong> Settings ›
                  Forwarding and POP/IMAP › Add a forwarding address. Then use a filter
                  (e.g. <em>subject:receipt OR from:stripe.com</em>) that forwards matching mail here.
                </li>
                <li>
                  <strong className="text-foreground">Auto-forward from Outlook / iCloud:</strong> same idea —
                  create a rule that forwards receipt emails to this address.
                </li>
                <li>
                  Every attachment (PDF or image) becomes a new row in your Document Inbox.
                  Email-body-only receipts are saved as HTML so nothing is lost.
                </li>
              </ol>
              <div className="text-xs text-muted-foreground pt-1">
                Works with Gmail, Outlook, iCloud, Yahoo, and anything that supports email forwarding —
                no per-provider OAuth needed.
              </div>
            </div>

            {/* Recent imports */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Recent auto-imports
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={simulate}
                  disabled={simulating}
                  data-testid="receipts-forwarding-simulate"
                >
                  {simulating ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Sending…</>
                  ) : (
                    <><Sparkles className="w-3 h-3 mr-1" />Send test receipt</>
                  )}
                </Button>
              </div>

              {(!data.recent_imports || data.recent_imports.length === 0) ? (
                <div className="text-sm text-muted-foreground bg-muted/20 rounded-md p-3">
                  No receipts imported yet. Forward one to the address above to get started,
                  or click &ldquo;Send test receipt&rdquo; for a dry run.
                </div>
              ) : (
                <ul className="space-y-2" data-testid="receipts-forwarding-recent">
                  {data.recent_imports.map((row) => (
                    <li
                      key={row.log_id}
                      className="text-sm rounded-md border border-border/50 bg-muted/10 p-3"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate">{row.subject || '(no subject)'}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            from {row.from_email || '?'}
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(row.received_at).toLocaleString()}
                        </div>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {row.documents_created?.length || 0} doc{(row.documents_created?.length || 0) === 1 ? '' : 's'} added
                        {row.notes?.length > 0 && (
                          <> · {row.notes.slice(0, 2).join(', ')}</>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* DNS status hint */}
            <div className="text-xs text-muted-foreground flex items-start gap-1.5 pt-2 border-t border-border/40">
              <ExternalLink className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span>
                Domain: <strong>{data.domain}</strong> — if the MX record isn&apos;t live yet in your account&apos;s DNS,
                real forwards will bounce. Use &ldquo;Send test receipt&rdquo; to verify the ingest pipeline in the meantime.
              </span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
